import { Search, Layers, Copy, Code2, Gauge, Database, GitBranch, Globe2 } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Technical SEO service page.
 * Distinct from /servicios/seo-diagnostico (a paid diagnostic deliverable)
 * and from /consultor-seo (the consultant/positioning page).
 */
export const technicalSeoPage: MoneyPageContent = {
  id: "technical-seo",
  icon: Code2,
  serviceType: "Technical SEO",
  esPath: "/servicios/seo-tecnico",
  enPath: "/en/services/technical-seo",
  es: {
    metaTitle: "SEO Técnico | Rastreo, indexación y arquitectura",
    metaDescription:
      "SEO técnico enfocado en que Google pueda rastrear, indexar y entender tu sitio: arquitectura, JavaScript, Core Web Vitals y datos estructurados.",
    keywords: [
      "seo técnico",
      "rastreo e indexación",
      "arquitectura web seo",
      "core web vitals",
      "javascript seo",
      "datos estructurados",
    ],
    h1: "SEO Técnico para construir una base que los buscadores puedan rastrear y entender",
    breadcrumbLabel: "SEO Técnico",
    heroSubtitle: "La base antes que la táctica",
    heroDescription:
      "El SEO técnico no es una lista de errores de una herramienta. Es la infraestructura que decide si tu contenido llega a rastrearse, indexarse y mostrarse correctamente. Trabajo la arquitectura, el rendimiento y la forma en que los buscadores procesan tu sitio, priorizando lo que realmente afecta al negocio.",
    ctaPrimary: {
      label: "Revisar problemas técnicos de mi sitio",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Rastreo e indexación",
        text: "Qué puede rastrear Google, qué indexa realmente y por qué hay páginas que quedan fuera.",
      },
      {
        label: "Arquitectura y JavaScript",
        text: "Estructura de enlazado interno y comportamiento de renderizado en sitios SPA/React.",
      },
      {
        label: "Rendimiento y Core Web Vitals",
        text: "Impacto de la velocidad y la experiencia de carga en cómo se evalúa tu sitio.",
      },
      {
        label: "Priorización por impacto",
        text: "No todos los errores técnicos pesan igual: se priorizan por efecto en negocio, no por severidad de herramienta.",
      },
    ],
    explanation: {
      title: "Qué es el SEO técnico (y qué no es)",
      definition:
        "El SEO técnico es el conjunto de decisiones de infraestructura, código y arquitectura que determinan si un buscador puede acceder a tu contenido, entenderlo y considerarlo apto para mostrarlo en resultados. No es solo corregir errores que marca un crawler: es entender cómo esos errores afectan al descubrimiento, la indexación y la experiencia real del usuario.",
      whyImportant: {
        title: "Por qué importa antes que el contenido o los enlaces",
        text: "Si Google no puede rastrear una sección, no la indexa. Si no la indexa, no compite por nada, sin importar cuán bueno sea el contenido. Los problemas técnicos limitan el techo de todo lo demás que se invierte en SEO.",
      },
      consequences: {
        title: "Qué pasa si se ignora",
        text: "Páginas importantes fuera del índice, presupuesto de rastreo desperdiciado en URLs irrelevantes, contenido duplicado compitiendo consigo mismo, y experiencias lentas que afectan tanto a usuarios como a la forma en que se evalúa el sitio.",
      },
    },
    problems: {
      title: "Problemas técnicos que trabajo con más frecuencia",
      subtitle: "Cada uno con un efecto distinto sobre visibilidad, no solo sobre un reporte",
      items: [
        {
          icon: Search,
          title: "Rastreo e indexación inconsistentes",
          description:
            "Páginas relevantes que Google no rastrea, indexa parcialmente o excluye por señales confusas en robots.txt, sitemaps o directivas noindex.",
          impact: "Contenido invisible aunque exista y esté bien escrito.",
        },
        {
          icon: Layers,
          title: "Arquitectura de sitio desordenada",
          description:
            "Jerarquías de enlazado interno que diluyen la autoridad de las páginas importantes o dejan contenido clave a demasiados clics de profundidad.",
          impact: "Páginas prioritarias compitiendo en desventaja frente a las secundarias.",
        },
        {
          icon: Copy,
          title: "Canonicalización y contenido duplicado",
          description:
            "URLs con parámetros, variantes de filtrado o versiones móviles/escritorio que generan duplicación y confunden qué versión debe indexarse.",
          impact: "Señales de relevancia repartidas entre varias URLs en lugar de consolidadas.",
        },
        {
          icon: Code2,
          title: "Renderizado en sitios JavaScript / SPA",
          description:
            "Contenido que depende de JavaScript del lado del cliente y que no se renderiza de forma confiable para los rastreadores, especialmente en frameworks tipo React.",
          impact: "Contenido y enlaces que el usuario ve pero el buscador no procesa igual.",
        },
        {
          icon: Gauge,
          title: "Rendimiento y Core Web Vitals",
          description:
            "Tiempos de carga, estabilidad visual e interactividad por debajo de lo esperado, afectando tanto la experiencia como la señal de calidad.",
          impact: "Fricción para el usuario y una variable más en cómo se evalúa el sitio.",
        },
        {
          icon: Database,
          title: "Datos estructurados ausentes o mal implementados",
          description:
            "Marcado schema.org incompleto, con errores o desalineado con el contenido visible de la página.",
          impact: "Menos oportunidades de resultados enriquecidos y señales de contexto más débiles.",
        },
        {
          icon: GitBranch,
          title: "Migraciones y rediseños mal planificados",
          description:
            "Cambios de dominio, plataforma o estructura de URLs sin un plan de redirecciones y validación posterior.",
          impact: "Pérdida de visibilidad e indexación acumulada durante meses.",
        },
        {
          icon: Globe2,
          title: "Implementación técnica internacional",
          description:
            "Etiquetas hreflang mal configuradas o inconsistentes entre versiones de idioma o país.",
          impact: "Google mostrando la versión incorrecta a cada mercado.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo el SEO técnico",
      subtitle: "Diagnóstico priorizado por impacto, no por cantidad de errores",
      steps: [
        {
          number: "01",
          title: "Revisión de rastreo e indexación",
          description:
            "Cómo accede Google al sitio hoy: qué rastrea, qué indexa y dónde se pierde presupuesto de rastreo.",
        },
        {
          number: "02",
          title: "Evaluación de arquitectura y renderizado",
          description:
            "Estructura de enlazado interno y comportamiento real del sitio ante rastreadores, incluyendo consideraciones específicas de JavaScript/SPA.",
        },
        {
          number: "03",
          title: "Priorización por impacto de negocio",
          description:
            "No todos los hallazgos técnicos tienen el mismo peso. Se ordenan según su efecto real en visibilidad e ingresos, no según la severidad que asigna una herramienta.",
        },
        {
          number: "04",
          title: "Plan de implementación",
          description:
            "Recomendaciones concretas y viables para tu equipo de desarrollo, con criterios claros de validación tras cada cambio.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Análisis de rastreo, indexación y presupuesto de rastreo",
          "Revisión de arquitectura y enlazado interno",
          "Evaluación de renderizado en sitios JavaScript/SPA",
          "Auditoría de canonicalización y contenido duplicado",
          "Revisión de Core Web Vitals y rendimiento",
          "Implementación y validación de datos estructurados",
          "Planificación técnica de migraciones y rediseños",
          "Configuración e implementación de hreflang",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Desarrollo o implementación directa del código (se coordina con tu equipo técnico)",
          "Un informe genérico de errores sin priorización",
          "Diseño visual o UX del sitio",
          "Auditoría profesional completa como entregable independiente (ver seo-diagnostico)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Sitios con problemas de indexación que no logran explicar",
          "Equipos que están migrando plataforma, dominio o estructura de URLs",
          "Sitios construidos en frameworks JavaScript (React y similares)",
          "Negocios con presencia en varios países o idiomas",
        ],
      },
      notFor: {
        title: "Para quién no es el mejor ajuste",
        items: [
          "Quien busca una revisión técnica gratuita y exhaustiva sin compromiso",
          "Sitios sin capacidad de desarrollo para implementar cambios",
          "Quien espera resultados de indexación inmediatos y garantizados",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿El SEO técnico es lo mismo que una auditoría SEO?",
          answer:
            "No. El SEO técnico es una línea de trabajo continua sobre infraestructura, arquitectura y rendimiento. La auditoría (seo-diagnostico) es un entregable puntual que documenta el estado del sitio en un momento dado. Muchas veces una auditoría identifica los problemas que después se resuelven con trabajo técnico.",
        },
        {
          question: "¿Todos los errores que marca una herramienta como Screaming Frog o Ahrefs son igual de urgentes?",
          answer:
            "No. Las herramientas asignan severidad de forma genérica. Un error de encabezados duplicados en páginas irrelevantes pesa mucho menos que un bloqueo de rastreo en las páginas que generan ingresos. La priorización correcta se hace por impacto en negocio, no por el color de alerta de la herramienta.",
        },
        {
          question: "¿Los sitios hechos en React o como SPA tienen desventaja en SEO?",
          answer:
            "Pueden tenerla si el renderizado no está bien resuelto. Google puede ejecutar JavaScript, pero no siempre de forma inmediata ni completa, y otros rastreadores tienen más limitaciones aún. Hay que validar cómo se sirve el contenido crítico y decidir si conviene renderizado del lado del servidor, pre-renderizado o ajustes específicos.",
        },
        {
          question: "¿Cuánto tiempo toma ver resultados de una mejora técnica?",
          answer:
            "Depende del problema, el tamaño del sitio y la frecuencia de rastreo de Google. Algunos cambios de indexación se reflejan en semanas; otros, como consolidación de arquitectura, toman más. No prometo plazos fijos porque dependen de factores fuera de mi control.",
        },
        {
          question: "¿Necesito acceso a mi desarrollador para este trabajo?",
          answer:
            "Sí, en la mayoría de los casos. Yo diagnostico, priorizo y especifico los cambios; la implementación técnica suele requerir a tu equipo de desarrollo o al mío en coordinación contigo.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "El SEO técnico se conecta con otras áreas del sitio",
      links: [
        {
          label: "Auditoría SEO profesional",
          description: "Un diagnóstico completo y documentado del estado actual de tu sitio.",
          to: "/servicios/seo-diagnostico",
        },
        {
          label: "SEO Internacional",
          description: "Arquitectura y señales técnicas para competir en varios países o idiomas.",
          to: "/servicios/seo-internacional",
        },
        {
          label: "Posicionamiento en IA/LLM",
          description: "Cómo los motores de IA generativa descubren y citan tu contenido.",
          to: "/servicios/posicionamiento-llm",
        },
        {
          label: "Consultoría SEO",
          description: "Estrategia y acompañamiento continuo más allá de lo técnico.",
          to: "/consultor-seo",
        },
        {
          label: "SEO para ecommerce",
          description: "Consideraciones técnicas específicas de catálogos y páginas de producto.",
          to: "/servicios/ecommerce-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de SEO técnico enfocado en rastreo, indexación, arquitectura, renderizado JavaScript, rendimiento y datos estructurados, con priorización por impacto de negocio.",
    keyPoints: [
      "No es una auditoría; es trabajo técnico continuo o por proyecto",
      "Cubre rastreo, indexación, arquitectura, JS/SPA, Core Web Vitals, datos estructurados",
      "Incluye migraciones/rediseños y SEO técnico internacional (hreflang)",
      "Prioriza por impacto de negocio, no por severidad genérica de herramienta",
    ],
  },
  en: {
    metaTitle: "Technical SEO Services | Crawling & Indexing",
    metaDescription:
      "Technical SEO focused on crawlability, indexing, architecture, JavaScript rendering, Core Web Vitals and structured data — prioritized by business impact.",
    keywords: [
      "technical seo",
      "crawling and indexing",
      "site architecture seo",
      "core web vitals",
      "javascript seo",
      "structured data",
    ],
    h1: "Technical SEO to build a foundation search engines can crawl and understand",
    breadcrumbLabel: "Technical SEO",
    heroSubtitle: "The foundation before the tactics",
    heroDescription:
      "Technical SEO isn't a dump of tool errors. It's the infrastructure that decides whether your content gets crawled, indexed and shown correctly in the first place. I work on architecture, performance and how search engines actually process your site, prioritizing what affects the business, not what a scanner flags as \"critical.\"",
    ctaPrimary: {
      label: "Review my site's technical issues",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Crawling & indexing",
        text: "What Google can actually crawl, what gets indexed, and why pages fall out.",
      },
      {
        label: "Architecture & JavaScript",
        text: "Internal linking structure and rendering behavior on React/SPA-style sites.",
      },
      {
        label: "Performance & Core Web Vitals",
        text: "How load speed and page experience factor into how your site is evaluated.",
      },
      {
        label: "Impact-based prioritization",
        text: "Not all technical errors carry the same weight — priority is set by business impact, not tool severity scores.",
      },
    ],
    explanation: {
      title: "What technical SEO is (and isn't)",
      definition:
        "Technical SEO is the set of infrastructure, code and architecture decisions that determine whether a search engine can access your content, understand it, and consider it eligible to rank. It's not just fixing whatever a crawler flags — it's understanding how those issues affect discovery, indexing and real user experience.",
      whyImportant: {
        title: "Why it comes before content or links",
        text: "If Google can't crawl a section, it doesn't get indexed. If it isn't indexed, it doesn't compete for anything, no matter how good the content is. Technical issues cap the ceiling on everything else you invest in SEO.",
      },
      consequences: {
        title: "What happens if it's ignored",
        text: "Important pages left out of the index, crawl budget wasted on irrelevant URLs, duplicate content competing with itself, and slow experiences that hurt both users and how the site gets evaluated.",
      },
    },
    problems: {
      title: "Technical issues I work with most often",
      subtitle: "Each with a different effect on visibility — not just a line in a report",
      items: [
        {
          icon: Search,
          title: "Inconsistent crawling & indexing",
          description:
            "Relevant pages Google doesn't crawl, only partially indexes, or excludes due to conflicting robots.txt, sitemap or noindex signals.",
          impact: "Content that's invisible even when it's well written.",
        },
        {
          icon: Layers,
          title: "Disorganized site architecture",
          description:
            "Internal linking hierarchies that dilute the authority of important pages or bury key content too many clicks deep.",
          impact: "Priority pages competing at a disadvantage against secondary ones.",
        },
        {
          icon: Copy,
          title: "Canonicalization & duplicate content",
          description:
            "Parameterized URLs, filter variants or desktop/mobile versions that create duplication and confuse which version should rank.",
          impact: "Relevance signals split across multiple URLs instead of consolidated.",
        },
        {
          icon: Code2,
          title: "Rendering on JavaScript / SPA sites",
          description:
            "Content that relies on client-side JavaScript and doesn't render reliably for crawlers, especially on React-based frameworks.",
          impact: "Content and links users see that search engines don't process the same way.",
        },
        {
          icon: Gauge,
          title: "Performance & Core Web Vitals",
          description:
            "Load times, visual stability and interactivity below expectations, affecting both experience and quality signals.",
          impact: "Friction for users and one more variable in how the site gets evaluated.",
        },
        {
          icon: Database,
          title: "Missing or broken structured data",
          description:
            "Incomplete schema.org markup, implementation errors, or markup misaligned with the page's visible content.",
          impact: "Fewer rich-result opportunities and weaker context signals.",
        },
        {
          icon: GitBranch,
          title: "Poorly planned migrations & redesigns",
          description:
            "Domain, platform or URL-structure changes without a redirect plan and post-launch validation.",
          impact: "Months of accumulated visibility and indexing lost.",
        },
        {
          icon: Globe2,
          title: "International technical implementation",
          description:
            "Hreflang tags that are misconfigured or inconsistent across language or country versions.",
          impact: "Google serving the wrong version to the wrong market.",
        },
      ],
    },
    methodology: {
      title: "How I approach technical SEO",
      subtitle: "Diagnosis prioritized by impact, not by error count",
      steps: [
        {
          number: "01",
          title: "Crawl & indexing review",
          description:
            "How Google accesses the site today: what it crawls, what it indexes, and where crawl budget is being wasted.",
        },
        {
          number: "02",
          title: "Architecture & rendering assessment",
          description:
            "Internal linking structure and how the site actually behaves for crawlers, including JavaScript/SPA-specific considerations.",
        },
        {
          number: "03",
          title: "Business-impact prioritization",
          description:
            "Not every technical finding carries the same weight. Findings are ranked by real effect on visibility and revenue, not by the severity level a tool assigns.",
        },
        {
          number: "04",
          title: "Implementation plan",
          description:
            "Concrete, actionable recommendations for your development team, with clear validation criteria after each change.",
        },
      ],
      ctaText: "Request a Strategic SEO Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What's included",
        items: [
          "Crawling, indexing and crawl-budget analysis",
          "Architecture and internal linking review",
          "Rendering assessment on JavaScript/SPA sites",
          "Canonicalization and duplicate content audit",
          "Core Web Vitals and performance review",
          "Structured data implementation and validation",
          "Technical planning for migrations and redesigns",
          "Hreflang setup and implementation",
        ],
      },
      notIncludes: {
        title: "What's not included",
        items: [
          "Direct code development or implementation (coordinated with your dev team)",
          "A generic error report with no prioritization",
          "Visual design or UX work",
          "A full professional audit as a standalone deliverable (see SEO Audit)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this is a good fit for",
        items: [
          "Sites with indexing issues they can't explain",
          "Teams migrating platform, domain or URL structure",
          "Sites built on JavaScript frameworks (React and similar)",
          "Businesses operating across multiple countries or languages",
        ],
      },
      notFor: {
        title: "Who this isn't the best fit for",
        items: [
          "Anyone looking for a free, exhaustive technical review with no commitment",
          "Sites without development capacity to implement changes",
          "Anyone expecting immediate, guaranteed indexing results",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Is technical SEO the same as an SEO audit?",
          answer:
            "No. Technical SEO is ongoing work on infrastructure, architecture and performance. The audit (SEO Audit service) is a point-in-time deliverable that documents the site's current state. Often an audit identifies the issues that technical work then resolves.",
        },
        {
          question: "Are all the errors a tool like Screaming Frog or Ahrefs flags equally urgent?",
          answer:
            "No. Tools assign severity generically. A duplicate-heading warning on irrelevant pages matters far less than a crawl block on revenue-generating pages. Real prioritization is done by business impact, not by the tool's alert color.",
        },
        {
          question: "Are React or SPA sites at a disadvantage in SEO?",
          answer:
            "They can be, if rendering isn't handled correctly. Google can execute JavaScript, but not always immediately or completely, and other crawlers are more limited still. You need to verify how critical content is served and decide whether server-side rendering, pre-rendering, or targeted fixes are needed.",
        },
        {
          question: "How long does it take to see results from a technical fix?",
          answer:
            "It depends on the issue, site size, and how often Google crawls the site. Some indexing changes show up within weeks; architecture consolidation takes longer. I don't promise fixed timelines because they depend on factors outside my control.",
        },
        {
          question: "Do I need my developer involved in this work?",
          answer:
            "Usually, yes. I diagnose, prioritize and specify the changes needed; implementation typically requires your development team, or mine working in coordination with you.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "Technical SEO connects to other parts of your site strategy",
      links: [
        {
          label: "Professional SEO Audit",
          description: "A full, documented diagnosis of your site's current state.",
          to: "/en/services/seo-audit",
        },
        {
          label: "International SEO",
          description: "Architecture and technical signals to compete across countries and languages.",
          to: "/en/services/international-seo",
        },
        {
          label: "AI Search / LLM Positioning",
          description: "How generative AI engines discover and cite your content.",
          to: "/en/services/llm-positioning",
        },
        {
          label: "SEO Consulting",
          description: "Ongoing strategy and guidance beyond the technical layer.",
          to: "/en/seo-consultant",
        },
        {
          label: "Ecommerce SEO",
          description: "Technical considerations specific to catalogs and product pages.",
          to: "/en/services/ecommerce-seo",
        },
      ],
    },
    llmSummary:
      "Technical SEO service covering crawling, indexing, architecture, JavaScript rendering, performance and structured data, prioritized by business impact.",
    keyPoints: [
      "Not an audit — ongoing or project-based technical work",
      "Covers crawling, indexing, architecture, JS/SPA, Core Web Vitals, structured data",
      "Includes migrations/redesigns and international technical SEO (hreflang)",
      "Prioritizes by business impact, not generic tool severity",
    ],
  },
};
